// src/utils/helpers.js

/**
 * Get appropriate icon for thinking step
 * @param {string} step - The step type or content
 * @returns {string} - Emoji icon
 */
export const getStepIcon = (step) => {
    if (!step || typeof step !== 'string') return '⚙️';

    const stepLower = step.toLowerCase();

    // Analysis and thinking
    if (stepLower.includes('analyze') || stepLower.includes('analysis')) return '💡';
    if (stepLower.includes('think') || stepLower.includes('reasoning')) return '🧠';
    if (stepLower.includes('understand') || stepLower.includes('comprehend')) return '🔍';

    // Data and information
    if (stepLower.includes('data') || stepLower.includes('information')) return '💾';
    if (stepLower.includes('search') || stepLower.includes('lookup')) return '🔎';
    if (stepLower.includes('fetch') || stepLower.includes('retrieve')) return '📥';

    // Financial specific
    if (stepLower.includes('fundamental') || stepLower.includes('finance')) return '🏦';
    if (stepLower.includes('technical') || stepLower.includes('chart')) return '📈';
    if (stepLower.includes('quantitative') || stepLower.includes('math')) return '🧮';
    if (stepLower.includes('market') || stepLower.includes('trading')) return '📊';
    if (stepLower.includes('price') || stepLower.includes('valuation')) return '💰';

    // Communication and processing
    if (stepLower.includes('dispatch') || stepLower.includes('router')) return '🔄';
    if (stepLower.includes('news') || stepLower.includes('article')) return '📰';
    if (stepLower.includes('aggregate') || stepLower.includes('combine')) return '📊';
    if (stepLower.includes('autogen') || stepLower.includes('debate')) return '🤝';
    if (stepLower.includes('generate') || stepLower.includes('create')) return '📝';
    if (stepLower.includes('critic') || stepLower.includes('review')) return '🧐';
    if (stepLower.includes('revise') || stepLower.includes('edit')) return '✍️';
    if (stepLower.includes('validate') || stepLower.includes('check')) return '✅';

    // Error handling
    if (stepLower.includes('error') || stepLower.includes('fail')) return '❌';
    if (stepLower.includes('warning') || stepLower.includes('alert')) return '⚠️';

    // Default
    return '⚙️';
};

/**
 * Format message history from API response
 * @param {Array} messages - Raw messages from API
 * @param {string} sessionId - Session ID for unique message IDs
 * @returns {Array} - Formatted messages
 */
export const formatMessageHistory = (messages, sessionId) => {
    if (!Array.isArray(messages)) return [];

    return messages.map((msg, index) => ({
        id: `${sessionId}-history-${index}`,
        type: msg.type === 'human' ? 'user' : 'ai_answer',
        content: msg?.data?.content || msg?.content || '',
        thinkingSteps: msg?.thinkingSteps || [],
        timestamp: msg?.timestamp || new Date().toISOString(),
    }));
};

/**
 * Create auto title from first message
 * @param {string} firstMessage - The first message content
 * @param {number} maxLength - Maximum characters in title (default: 150)
 * @returns {string} - Generated title
 */
export const createAutoTitle = (firstMessage, maxLength = 150) => {
    if (!firstMessage || typeof firstMessage !== 'string') {
        return 'New Chat';
    }

    let trimmed = firstMessage.trim();
    
    // Capitalize first letter
    if (trimmed.length > 0) {
        trimmed = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
    }
    
    if (trimmed.length <= maxLength) {
        return trimmed;
    }

    // Cut at word boundary if possible
    const truncated = trimmed.substring(0, maxLength);
    const lastSpaceIndex = truncated.lastIndexOf(' ');
    
    if (lastSpaceIndex > maxLength * 0.8) {
        return truncated.substring(0, lastSpaceIndex) + '...';
    }
    
    return truncated + '...';
};

/**
 * Debounce function for search/input handling
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function
 */
export const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

/**
 * Format timestamp for display
 * @param {string|Date} timestamp - Timestamp to format
 * @returns {string} - Formatted timestamp
 */
export const formatTimestamp = (timestamp) => {
    try {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffMins < 1) return 'Vừa xong';
        if (diffMins < 60) return `${diffMins} phút trước`;
        if (diffHours < 24) return `${diffHours} giờ trước`;
        if (diffDays < 7) return `${diffDays} ngày trước`;

        return date.toLocaleDateString('vi-VN');
    } catch {
        return 'Không xác định';
    }
};

/**
 * Validate message content
 * @param {string} content - Message content to validate
 * @returns {Object} - Validation result
 */
export const validateMessage = (content) => {
    if (!content || typeof content !== 'string') {
        return { isValid: false, error: 'Nội dung tin nhắn không được để trống' };
    }

    const trimmed = content.trim();

    if (trimmed.length === 0) {
        return { isValid: false, error: 'Nội dung tin nhắn không được để trống' };
    }

    if (trimmed.length > 2000) {
        return { isValid: false, error: 'Nội dung tin nhắn không được vượt quá 2000 ký tự' };
    }

    return { isValid: true, content: trimmed };
};

/**
 * Sanitize session name
 * @param {string} name - Session name to sanitize
 * @returns {string} - Sanitized name
 */
export const sanitizeSessionName = (name) => {
    if (!name || typeof name !== 'string') return 'New Chat';

    const sanitized = name
        .trim()
        .replace(/[<>:"/\\|?*]/g, '') // Remove invalid filename characters
        .substring(0, 50); // Limit length

    return sanitized || 'New Chat';
};

/**
 * Check if user is on mobile device
 * @returns {boolean} - True if mobile
 */
export const isMobile = () => {
    return window.innerWidth < 768;
};

/**
 * Scroll to element smoothly
 * @param {HTMLElement} element - Element to scroll to
 * @param {string} behavior - Scroll behavior (smooth, auto, instant)
 */
export const scrollToElement = (element, behavior = 'smooth') => {
    if (element) {
        element.scrollIntoView({ behavior, block: 'end' });
    }
};

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} - Success status
 */
export const copyToClipboard = async (text) => {
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
            return true;
        } else {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            const result = document.execCommand('copy');
            textArea.remove();
            return result;
        }
    } catch (error) {
        console.error('Failed to copy text:', error);
        return false;
    }
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size
 */
export const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

/**
 * Generate random ID
 * @param {number} length - Length of ID (default: 10)
 * @returns {string} - Random ID
 */
export const generateId = (length = 10) => {
    return Math.random().toString(36).substring(2, 2 + length);
};

/**
 * Format tool name from snake_case to sentence case
 * @param {string} snakeCaseName - Tool name in snake_case (e.g., "get_company_news")
 * @returns {string} - Formatted name in sentence case (e.g., "Get company news")
 */
export const formatToolName = (snakeCaseName) => {
    if (!snakeCaseName || typeof snakeCaseName !== 'string') return 'Tool';
    const words = snakeCaseName.split('_').join(' ').toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
};

const INLINE_TOOL_PARAMS = {
    web_search: 'query',
    web_fetch: 'url',
};

export const buildFallbackToolName = (toolName, args = {}) => {
    const name = formatToolName(toolName);
    const paramKey = INLINE_TOOL_PARAMS[toolName];
    if (paramKey) {
        const value = args[paramKey];
        if (value) return `${name}: ${value}`;
    }
    return name;
};

/**
 * Local storage helpers with error handling
 */
export const storage = {
    get: (key, defaultValue = null) => {
        try {
            const item = localStorage.getItem(key);
            return item ? JSON.parse(item) : defaultValue;
        } catch {
            return defaultValue;
        }
    },

    set: (key, value) => {
        try {
            localStorage.setItem(key, JSON.stringify(value));
            return true;
        } catch {
            return false;
        }
    },

    remove: (key) => {
        try {
            localStorage.removeItem(key);
            return true;
        } catch {
            return false;
        }
    }
};